import { Modal, Tabs, Input, Row, Col, Form, Divider, Select, DatePicker, Popconfirm, Button, Space, Checkbox, Radio, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { PlusSquareOutlined, MinusSquareOutlined, CheckOutlined, WarningOutlined, ExclamationCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { useState, useContext } from 'react'
import { isEqual } from 'lodash';
import { UserContext } from '../../contexts/userContext'
import { db } from '../../firebase'
import { sourceNameLookup } from '../../helpers/sources'
import { ValidatedLabel, BlueIconOnHover } from './style'

export const LeadShareParterModal = ({ partner, onOk, onCancel }) => {
  const { user } = useContext(UserContext)
  const isTestUser = process.env.REACT_APP_TEST_USERS?.includes(user.email)
  const [form] = Form.useForm()
  const [testLeadShareForm] = Form.useForm()
  const [backfillForm] = Form.useForm()
  const [isDirty, setIsDirty] = useState(!partner.id)
  const [formTabWithErrors, setFormTabWithErrors] = useState([])
  const [showConfirmDiscard, setShowConfirmDiscard] = useState(false)
  const [textAreaAutoSize, setTextAreaAutoSize] = useState({});
  const [testLeadShareStatus, setTestLeadShareStatus] = useState({})

  // TODO maybe move these to a utils file?
  const validateVariables = async (field, value) => {
    const captured = value?.match(/\$(\w+(\.\w+)?)/g) || []

    for (const variable of captured) {
      const [k, v] = variable.split('.')

      if (k === '$lead') {
        if (!['email', 'offer', 'affId', 'affIdHashed', 'uid'].includes(v)) {
          return Promise.reject(`Incorrect $lead key '${v || ''}'`)
        }
      } else if (k === '$secrets') {
        // can't lookup secrets defined for functions 
        // if (!Object.keys(process.env).includes(v)) {

        if (!v) {
          return Promise.reject(`Incorrect $secret key '${v || ''}'`)
        }
      } else {
        return Promise.reject(`Incorrect variable ${k}`)
      }
    }

    return Promise.resolve()
  }

  const validatePositiveInteger = async (field, value) => {
    try {
      if (value) {
        const parsed = parseInt(value)
        if (isNaN(parsed) || parsed < 0) throw Error()
      }
      return Promise.resolve()
    } catch (error) {
      return Promise.reject('Must be positive number')
    }
  }

  const validateUrl = async (field, value) => {
    try {
      if (value) new URL(value)
      return Promise.resolve()
    } catch (error) {
      return Promise.reject('Invalid url')
    }
  }

  const validateRegex = async (field, value) => {
    try {
      if (value) new RegExp(value)
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const validateJSON = async (field, value) => {
    try {
      if (value) JSON.parse(value)
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  }

  const submitForm = async () => {
    try {
      setFormTabWithErrors([])
      const ret = await form.validateFields()
      // console.log('submitForm', { ret: JSON.stringify(ret) })

      if (ret.max_daily === '') ret.max_daily = null
      else if (ret.max_daily) ret.max_daily = parseInt(ret.max_daily)

      if (ret.count === '') ret.count = null
      else if (ret.count) ret.count = parseInt(ret.count)

      if (ret.total_count === '') ret.total_count = null
      else if (ret.total_count) ret.total_count = parseInt(ret.total_count)

      if (ret.value_formatted === '') ret.value = null
      else if (ret.value_formatted) ret.value = Math.floor(Number(ret.value_formatted) * 100)
      delete ret.value_formatted

      if (ret.daily_value_formatted === '') ret.daily_value = null
      else if (ret.daily_value_formatted) ret.daily_value = Math.floor(Number(ret.daily_value_formatted) * 100)
      delete ret.daily_value_formatted

      if (ret.total_value_formatted === '') ret.total_value = null
      else if (ret.total_value_formatted) ret.total_value = Math.floor(Number(ret.total_value_formatted) * 100)
      delete ret.total_value_formatted

      if (ret.expect_response_status === '') ret.expect_response_status = null
      else if (ret.expect_response_status) ret.expect_response_status = parseInt(ret.expect_response_status)

      if (ret.last_timestamp_dayjs === "") ret.last_timestamp = null
      else if (ret.last_timestamp_dayjs) ret.last_timestamp = db.firestore.Timestamp.fromDate(ret.last_timestamp_dayjs.toDate())
      delete ret.last_timestamp_dayjs

      if (ret.send_data_formatted === "") ret.send_data = null
      else if (ret.send_data_formatted) ret.send_data = JSON.stringify(JSON.parse(ret.send_data_formatted))
      delete ret.send_data_formatted

      if (ret.send_headers_formatted === "") ret.send_headers = null
      else if (ret.send_headers_formatted) ret.send_headers = JSON.parse(ret.send_headers_formatted)
      delete ret.send_headers_formatted

      // console.log('submitForm', { ret })

      // remove all entries with undefined values (keep empty string/false/null)
      const cleanedRet = Object.entries(ret)
        .filter(([k, v]) => v !== undefined)
        .reduce((o, [k, v]) => Object.assign(o, { [k]: v }), {})

      const backfill = await backfillForm.validateFields()
      // console.log('submitForm', { backfill })

      const cleanedBackfill = Object.entries(backfill)
        .map(([k, v]) => [k, Boolean(v)])
        .reduce((o, [k, v]) => Object.assign(o, { [k]: v }), {})
      // console.log('submitForm', { cleanedBackfill })

      const cleanedRetAndBackfill = {
        ...cleanedRet,
        ...Object.keys(cleanedBackfill).length > 0 && { backfill: cleanedBackfill }
      }

      // just save the difference (for existing ones)
      const updated = ret.id
        ? Object.entries(cleanedRetAndBackfill)
          .filter(([k, v]) => !['created_at', 'updated_at'].includes(k))
          .filter(([k, v]) => !isEqual(v, partner[k]))
          .reduce((o, [k, v]) => Object.assign(o, { [k]: v }), {})
        : cleanedRetAndBackfill

      // console.log('submitForm', { partner, updated })

      onOk && await onOk({
        id: partner.id,
        ...updated,
      })
    } catch (error) {
      console.error('form submit', error)

      if (error.errorFields) {
        // pick out which tab had errors
        error.errorFields?.forEach((e) => {
          if (['name', 'last_timestamp_dayjs'].includes(e.name[0])) {
            setFormTabWithErrors((tabs) => [...tabs, 'info'])
          } else if (['share_to', 'send_url', 'send_data_formatted', 'send_headers_formatted'].includes(e.name[0])) {
            setFormTabWithErrors((tabs) => [...tabs, 'lead_share'])
          }
        })
      } else {
        alert(error)
      }
    }
  }

  const cancelForm = (force) => {
    if (force || !isDirty) {
      setShowConfirmDiscard(false)
      onCancel && onCancel()
      return
    }

    setShowConfirmDiscard(!showConfirmDiscard)
  }

  // TODO let's move these somewhere better
  const testLeadShare = async () => {
    setTestLeadShareStatus((v) => ({ inProgress: true }))

    try {
      const lead = await testLeadShareForm.validateFields()
      const config = { ...partner, ...form.getFieldsValue() }
      console.log('testLeadShare', { config, lead })

      if (config.expect_response_status === '') config.expect_response_status = null
      else if (config.expect_response_status) config.expect_response_status = parseInt(config.expect_response_status)

      const res = await fetch(process.env.REACT_APP_TEST_SEND_URL, {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          "mode": "test",
          "partner": config.id,
          lead,
          config,
        }),
      })

      const contentType = res.headers.get('content-type')
      console.log('testLeadShare', { res, contentType })

      if (res.status != 200) {
        throw Error({ message: res.statusText })
      }

      const data = await res.json();
      console.log('testLeadShare', data)

      setTestLeadShareStatus((v) => ({
        ...v,
        ...data,
        warning: data.logs?.some((log) => log[0] == 'warn'),
        error: data.logs?.some((log) => log[0] == 'error')
      }))

    } catch (error) {
      console.error('ERROR testLeadShare', error)
      setTestLeadShareStatus((v) => ({ ...v, error }))
    } finally {
      setTestLeadShareStatus((v) => ({ ...v, inProgress: false }))
    }
  }

  const TextAreaAutoSizeLabel = ({ name, label, color }) => {
    return (
      <BlueIconOnHover
        style={{ display: 'flex', alignItems: 'center', gap: '0.15rem', cursor: 'pointer', color }}
        onClick={() => setTextAreaAutoSize((v) => ({
          ...v, [name]: !v[name]
        }))}
      >
        {label}
        <a style={{ fontSize: '90%' }}>{!textAreaAutoSize[name] && <PlusSquareOutlined />}{textAreaAutoSize[name] && <MinusSquareOutlined />}</a>
      </BlueIconOnHover>
    )
  }

  const numIncludeCondition = (partner) => [partner.include_lead_function, partner.suppression_list]
    .concat(partner.include_all_lead)
    .concat(partner.include_any_lead)
    .concat(partner.exclude_all_lead)
    .concat(partner.exclude_any_lead)
    .filter(Boolean)
    .length

  const IncludeExcludeLabel = ({ partner }) => {
    const num = numIncludeCondition(partner)
    const isAny = num === 1 && partner.include_any_lead?.length === 1 && partner.include_any_lead[0] === '*' || partner.include_all_lead?.length === 1 && partner.include_all_lead[0] === '*'

    return (
      <span>Include Conditions{num > 0 && !isAny ? ` (${num})` : ''}</span>
    )
  }

  const LeadInclusionConditionButton = ({ condition }) => {
    let label = condition

    if (condition === '*') label = 'any lead'

    if (condition.startsWith('affId:')) {
      const sourceName = sourceNameLookup({ affId: condition.split(':')[1]?.trim() })
      if (sourceName) label = `source: ${sourceName}`
    }

    return (
      <Button size='small' shape='round' variant={label === 'any lead' ? 'filled' : 'solid'} color='default' style={{ cursor: 'default' }}>
        <Tooltip title={label !== condition ? condition : undefined}>
          {label}
        </Tooltip>
      </Button>
    )
  }

  const items = [
    {
      key: 'info',
      label: <ValidatedLabel data-error={formTabWithErrors.includes('info')}>Info</ValidatedLabel>,
      forceRender: true,
      children: (
        <Form
          form={form}
          name='info'
          style={{ maxWidth: 600 }}
          autoComplete='off'
          layout='vertical'
          initialValues={{
            ...partner,
            ...partner?.last_timestamp && { last_timestamp_dayjs: dayjs(partner.last_timestamp.toDate()) },
            value_formatted: partner.value
              ? (Number(partner.value) / 100).toFixed(2)
              : undefined,
            daily_value_formatted: partner.daily_value
              ? (Number(partner.daily_value) / 100).toFixed(2)
              : undefined,
            total_value_formatted: partner.total_value
              ? (Number(partner.total_value) / 100).toFixed(2)
              : undefined,
          }}
          onChange={() => setIsDirty(form.isFieldsTouched())}
        >
          <Row justify='space-between'>
            <Col span={9}>
              <Form.Item
                label='ID'
                name='id'
              >
                <Input variant='filled' readOnly />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item
                label='Name'
                name='name'
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row justify='space-between'>
            <Col span={6}>
              <Form.Item
                label='Status'
                name='status'
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'paused', label: 'Paused' },
                    { value: 'deleted', label: 'Deleted' },
                  ]}
                  onChange={() => setIsDirty(form.isFieldsTouched())}
                />
              </Form.Item>
            </Col>
            <Col span={17}>
              <Form.Item
                label='Last Timestamp'
                name='last_timestamp_dayjs'
                rules={[{ required: true, message: 'Required' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  showTime
                  onChange={() => setIsDirty(form.isFieldsTouched())}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify='space-between'>
            <Col span={7}>
              <Form.Item
                label='Max Daily'
                name='max_daily'
                rules={[{ validator: validatePositiveInteger }]}
              >
                <Input className='col' type='number' />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                label='Count'
                name='count'
              >
                <Input className='col' type='number' variant='filled' readOnly={!isTestUser} />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                label='Total Count'
                name='total_count'
              >
                <Input className='col' type='number' variant='filled' readOnly={!isTestUser} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify='space-between'>
            <Col span={7}>
              <Form.Item
                label='CPL'
                name='value_formatted'
                rules={[{ validator: validatePositiveInteger }]}
              >
                <Input className='col' prefix='$' min="0.01" step="0.01" type="number" />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                label='Daily Revenue'
                name='daily_value_formatted'
              >
                <Input className='col' prefix='$' min="0.01" step="0.01" type='number' variant='filled' readOnly={!isTestUser} />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                label='Total Revenue'
                name='total_value_formatted'
              >
                <Input className='col' prefix='$' min="0.01" step="0.01" type='number' variant='filled' readOnly={!isTestUser} />
              </Form.Item>
            </Col>
          </Row>
        </Form >
      ),
    },
    {
      key: 'lead_share',
      label: <ValidatedLabel data-error={formTabWithErrors.includes('lead_share')}>Lead Share</ValidatedLabel>,
      forceRender: true,
      children: (
        <Form
          form={form}
          name='lead_share'
          style={{ maxWidth: 600 }}
          autoComplete='off'
          layout='vertical'
          initialValues={{
            ...partner,
            send_data_formatted: partner.send_data
              ? typeof (partner.send_data) === 'object'
                ? JSON.stringify(partner.send_data)
                : JSON.stringify(JSON.parse(partner.send_data || '{}'), undefined, '  ')
              : undefined,
            send_headers_formatted: partner.send_headers
              ? JSON.stringify(partner.send_headers, undefined, '  ')
              : undefined,
          }}
          onChange={() => setIsDirty(form.isFieldsTouched())}
        >
          <Row justify='space-between'>
            <Col span={16}>
              <Form.Item
                label='Share To'
                name='share_to'
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item
                label='Share Entity'
                name='share_entity'
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {partner.send_function &&
            <>
              <Divider orientation='center'>Custom Send Logic</Divider>

              <Form.Item
                label='Function Name'
                name='send_function'
                tooltip="For custom lead share logic - the function must be setup in code"
                rules={[{
                  validator(field, value) {
                    if (!value && !form.getFieldValue('send_url')) {
                      return Promise.reject("One of either send_url or send_function is required")
                    }
                    return Promise.resolve()
                  }
                }]}
              >
                <Input variant='filled' readOnly={!isTestUser} />
              </Form.Item>
            </>
          }
          {!partner.send_function &&
            <>
              <Divider orientation='center'>Request</Divider>

              <Row justify='space-between'>
                <Col span={4}>
                  <Form.Item
                    label='Method'
                    name='send_method'
                  >
                    <Select
                      defaultValue={partner.send_url ? 'GET' : undefined}
                      options={[
                        { value: 'GET', label: 'GET' },
                        { value: 'POST', label: 'POST' },
                        { value: 'PUT', label: 'PUT' },
                      ]}
                      onChange={() => setIsDirty(form.isFieldsTouched())}
                    />
                  </Form.Item>
                </Col>
                <Col span={19}>
                  <Form.Item
                    label='URL'
                    name='send_url'
                    tooltip="Variables: $lead.email, $lead.offer, $lead.affId, $lead.affIdHashed, $lead.uid and $secrets.<SECRET>"
                    rules={[{
                      validator(field, value) {
                        if (value) {
                          return Promise.all([
                            validateUrl(field, value),
                            validateVariables(field, value),
                          ])
                        }

                        if (!value && !form.getFieldValue('send_function')) {
                          return Promise.reject("One of either send_url or send_function is required")
                        }

                        return Promise.resolve()
                      }
                    }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label={<TextAreaAutoSizeLabel name='lead_share.send_data_formatted' label='Data (JSON)' />}
                name='send_data_formatted'
                tooltip="Variables: $lead.email, $lead.offer, $lead.affId, $lead.uid and $secrets.<SECRET>"
                rules={[{
                  validator: (field, value) => Promise.all([
                    validateJSON(field, value),
                    validateVariables(field, value),
                  ])
                }]}
              >
                <Input.TextArea
                  rows={3}
                  autoSize={textAreaAutoSize['lead_share.send_data_formatted']}
                  resize="vertical"
                />
              </Form.Item>
              <Form.Item
                label={<TextAreaAutoSizeLabel name='lead_share.send_headers_formatted' label='Headers (JSON)' />}
                name='send_headers_formatted'
                tooltip="Variables: $lead.email, $lead.offer, $lead.affId, $lead.uid and $secrets.<SECRET>"
                rules={[{
                  validator: (field, value) => Promise.all([
                    validateJSON(field, value),
                    validateVariables(field, value),
                  ])
                }]}
              >
                <Input.TextArea rows={3}
                  autoSize={textAreaAutoSize['lead_share.send_headers_formatted']}
                  resize="vertical"
                />
              </Form.Item>

              <Divider orientation='center'>Expect Response</Divider>

              <Row justify='space-between'>
                <Col span={4}>
                  <Form.Item
                    label='Status'
                    name='expect_response_status'
                    tooltip="Typically 200 or 202"

                  >
                    <Input type='number' />
                  </Form.Item>
                </Col>
                <Col span={19}>
                  <Form.Item
                    label='Data (Exact Match)'
                    name='expect_response_data'
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label='Data Matches (Regular Expression)'
                name='expect_response_data_matches'
                rules={[{ validator: validateRegex }]}
              >
                <Input />
              </Form.Item>
            </>
          }
        </Form>
      ),
    },
    {
      key: 'include_exclude',
      label: <IncludeExcludeLabel partner={partner} />,
      forceRender: true,
      children: (
        <div>
          <Form
            form={form}
            name='include_exclude'
            style={{ maxWidth: 600 }}
            autoComplete='off'
            layout='vertical'
            initialValues={{
              ...partner
            }}
            onChange={() => setIsDirty(form.isFieldsTouched())}
          >
            <Form.Item
              label='Share Queue'
              name='share_queue'>
              <Radio.Group
                block
                options={[
                  { label: 'Round Robin', value: 'round-robin' },
                  { label: 'Internal', value: 'internal' },
                  { label: 'Backfill', value: 'backfill' },
                  { label: 'Backfill 1-24hrs', value: 'backfill-24' },
                ]}
                defaultValue="round-robin"
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>

            {partner.include_lead_function &&
              <Form.Item
                label='Custom Inclusion Function'
                name='include_lead_function'>
                <Input variant='filled' readOnly />
              </Form.Item>
            }

            {!partner.include_lead_function && !partner.include_any_lead?.length && !partner.include_all_lead?.length && !partner.exclude_any_lead?.length && !partner.exclude_all_lead?.length &&
              <Form.Item
                label={`Include`}
                name='include_any_lead'>
                <Space wrap={true}>
                  <LeadInclusionConditionButton condition="any lead" />
                  {/* <Button size="small" color="default" variant="link" style={{ background: "", marginLeft: "-0.5rem" }}><PlusCircleOutlined /></Button> */}
                </Space>
              </Form.Item>
            }
            {!partner.include_lead_function && partner.include_any_lead?.length > 0 &&
              <Form.Item
                label={`Include${partner.include_any_lead?.length > 1 ? ' Any' : ''}`}
                name='include_any_lead'>
                <Space wrap={true}>
                  {partner.include_any_lead?.sort()?.map((cond, i) => (
                    <LeadInclusionConditionButton key={i} condition={cond} />
                  ))}
                  {/* <Button size="small" color="default" variant="link" style={{ background: "", marginLeft: "-0.5rem" }}><PlusCircleOutlined /></Button> */}
                </Space>
              </Form.Item>
            }
            {!partner.includeLeadsFunction && partner.include_all_lead?.length > 0 &&
              <Form.Item
                label={`Include${partner.include_all_lead?.length > 1 ? ' All' : ''}`}
                name='include_all_lead'>
                <Space wrap={true}>
                  {partner.include_all_lead?.sort()?.map((cond, i) => (
                    <LeadInclusionConditionButton key={i} condition={cond} />
                  ))}
                  {/* <Button size="small" color="default" variant="link" style={{ background: "", marginLeft: "-0.5rem" }}><PlusCircleOutlined /></Button> */}
                </Space>
              </Form.Item>
            }
            {!partner.includeLeadsFunction && partner.exclude_any_lead?.length > 0 &&
              <Form.Item
                label={`Exclude${partner.exclude_any_lead?.length > 1 ? ' Any' : ''}`}
                name='exclude_any_lead'>
                <Space wrap={true}>
                  {partner.exclude_any_lead?.sort()?.map((cond, i) => (
                    <LeadInclusionConditionButton key={i} condition={cond} />
                  ))}
                  {/* <Button size="small" color="default" variant="link" style={{ background: "", marginLeft: "-0.5rem" }}><PlusCircleOutlined /></Button> */}
                </Space>
              </Form.Item>
            }
            {!partner.includeLeadsFunction && partner.exclude_all_lead?.length > 0 &&
              <Form.Item
                label={`Exclude${partner.exclude_all_lead?.length > 1 ? ' All' : ''}`}
                name='exclude_all_lead'>
                <Space wrap={true}>
                  {partner.exclude_all_lead?.sort()?.map((cond, i) => (
                    <LeadInclusionConditionButton key={i} condition={cond} />
                  ))}
                  {/* <Button size="small" color="default" variant="link" style={{ background: "", marginLeft: "-0.5rem" }}><PlusCircleOutlined /></Button> */}
                </Space>
              </Form.Item>
            }

            {partner.suppression_list &&
              <Form.Item
                label='Suppression List'
                name='suppression_list'>
                <Input variant='filled' readOnly />
              </Form.Item>
            }

          </Form>
        </div >
      ),
    },
    partner.backfill && {
      key: 'backfill',
      label: 'Backfill',
      forceRender: true,
      children: (
        <div>
          <Form
            form={backfillForm}
            name='backfill'
            style={{ maxWidth: 600 }}
            autoComplete='off'
            layout='vertical'
            initialValues={{
              ...partner.backfill
            }}
            onChange={() => setIsDirty(backfillForm.isFieldsTouched())}
          >
            {partner.backfill && Object.entries(partner.backfill).sort((a, b) => a[0].localeCompare(b[0])).map(([k, v]) => (
              <Form.Item
                key={k}
                label={k}
                name={k}
                valuePropName='checked'
              >
                <Checkbox>
                  Enabled
                </Checkbox>
              </Form.Item>
            ))}
          </Form>
        </div >
      ),
    },
    {
      key: 'test',
      label: 'Test',
      forceRender: true,
      children: (
        <Form
          form={testLeadShareForm}
          name='lead_share'
          style={{ maxWidth: 600 }}
          autoComplete='off'
          layout='vertical'
          initialValues={{
            affId: 'TEST_AFFID',
            list: 'TEST_LIST',
            offer: 'TEST_OFFER',
          }}
          onChange={() => setIsDirty(form.isFieldsTouched())}
        >
          <Divider orientation='center'>Test Lead</Divider>

          <Row justify='space-between' gutter='16'>
            <Col flex='auto'>
              <Form.Item
                name='email'
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input
                  placeholder="email@example.com"
                  type="email"
                />
              </Form.Item>
            </Col>
            <Col >
              <Button
                color={testLeadShareStatus.success ? 'green' : testLeadShareStatus.warning ? 'orange' : testLeadShareStatus.error ? 'danger' : 'primary'}
                variant='solid'
                loading={testLeadShareStatus.inProgress}
                onClick={testLeadShare}
              >
                {testLeadShareStatus.success && <CheckOutlined />}
                {testLeadShareStatus.warning && <WarningOutlined />}
                {testLeadShareStatus.error && <ExclamationCircleOutlined />}
                Send
              </Button>
            </Col>
          </Row>

          <Row justify='space-between'>
            <Col >
              <Form.Item
                label='Affiliate ID'
                name='affId'
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col >
              <Form.Item
                label='List'
                name='list'
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col >
              <Form.Item
                label='Offer'
                name='offer'
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
            </Col>

          </Row>

          {(testLeadShareStatus.success || testLeadShareStatus.request || testLeadShareStatus.response) &&
            <Divider orientation='center'>Request/Response</Divider>
          }

          {testLeadShareStatus.success &&
            <Form.Item
              label='Success'
              name='success'
            >
              <Input
                variant='filled'
                readOnly
                defaultValue={testLeadShareStatus.success}
              />
            </Form.Item>
          }
          {testLeadShareStatus.request &&
            <Form.Item
              label={<TextAreaAutoSizeLabel name='test.request' label='Request' />}
              name='request'
            >
              <Input.TextArea
                variant='filled'
                readOnly
                defaultValue={JSON.stringify(testLeadShareStatus.request, undefined, "  ")}
                rows={4}
                autoSize={textAreaAutoSize['test.request']}
                resize="vertical"
              />
            </Form.Item>
          }
          {testLeadShareStatus.response &&
            <Form.Item
              label={<TextAreaAutoSizeLabel name='test.response' label='Response' />}
              name='response'
            >
              <Input.TextArea
                variant='filled'
                readOnly
                defaultValue={JSON.stringify(testLeadShareStatus.response, undefined, "  ")}
                rows={4}
                autoSize={textAreaAutoSize['test.response']}
                resize="vertical"
              />
            </Form.Item>
          }

          {testLeadShareStatus.logs?.length &&
            <Divider orientation='center'>Logs</Divider>
          }
          {testLeadShareStatus.logs?.map((log, i) =>
            <Form.Item
              key={i}
              label={<TextAreaAutoSizeLabel
                name={`test.log-${i}`}
                label={`[${log[0].toUpperCase()}] ${[log[1][0]]}`}
                color={log[0] == 'error' ? '#ff4d4f' : log[0] == 'warn' ? '#faad14' : ''}
              />}
              name={`log-${i}`}
            >
              <Input.TextArea
                variant='filled'
                status={log[0] == 'error' ? 'error' : log[0] == 'warn' ? 'warning' : ''}
                readOnly
                rows={2}
                autoSize={textAreaAutoSize[`test.log-${i}`]}
                style={{ width: "100%" }}
                resize="vertical"
                defaultValue={JSON.stringify(log[1][1], undefined, '  ')}
              />
            </Form.Item>
          )}
        </Form>
      ),
    },
    isTestUser && {
      key: 'json',
      label: 'JSON',
      children: <Input.TextArea
        variant='filled'
        readOnly
        defaultValue={JSON.stringify(partner, undefined, "  ")}
        rows="12" style={{ width: "100%" }}
        resize="vertical"
      />,
    },
  ].filter(Boolean);

  return (
    <div>
      <Modal
        title={partner.id ? `Configure: ${partner?.name}` : 'New LeadShare'}
        width={{
          xs: '90%',
          sm: '80%',
          md: '70%',
          lg: '60%',
          xl: '50%',
          xxl: '40%',
        }}
        open={!!partner}

        cancelText={
          <Popconfirm
            open={showConfirmDiscard}
            title="Discard changes?"
            description="Are you sure you want to discard changes?"
            onCancel={() => cancelForm()}
            onConfirm={() => cancelForm(true)}
            okText="Yes"
            cancelText="No"
          >
            Cancel
          </Popconfirm>
        }
        cancelButtonProps={() => cancelForm()}
        onCancel={() => cancelForm()}

        okText='Save'
        okButtonProps={{ disabled: !isDirty }}
        onOk={submitForm}
      >
        <Tabs items={items} />
      </Modal>
    </div>
  )
}
